import type {
  ComponentValidationResult,
  HipaaPackageSchema,
  MissingFieldInfo,
  PackageComponentSchema,
} from '@/services/api/hipaaPackageService';

import { StaticDocumentLabels } from '../package-edit/constants';

const isWordDocumentName = (name: string | null | undefined): boolean => {
  const normalizedName = (name || '').toLowerCase();
  return (
    normalizedName.endsWith('.docx') || normalizedName.endsWith('.doc')
  );
};

export const isOnlyOfficeEditablePackageComponent = (
  component: PackageComponentSchema,
): boolean => {
  if (
    component.component_type === 'signed_authorization_merge' ||
    component.component_type === 'static_document'
  ) {
    return false;
  }

  switch (component.component_type) {
    case 'template':
    case 'global_template':
      return true;
    case 'matter_blob':
      return isWordDocumentName(
        component.blob?.original_filename || component.blob?.name,
      );
    default:
      return false;
  }
};

export const isVisiblePackageComponent = (
  component: PackageComponentSchema,
): boolean => {
  if (
    component.rendered_docx_url ||
    component.rendered_pdf_url ||
    component.component_type === 'template' ||
    component.component_type === 'global_template' ||
    component.component_type === 'signed_authorization_merge'
  ) {
    return true;
  }

  if (component.component_type === 'matter_blob') {
    return Boolean(component.blob_id);
  }

  return false;
};

export const getPackageComponentTitle = (
  component: PackageComponentSchema,
): string => {
  switch (component.component_type) {
    case 'template':
    case 'global_template':
      return (
        component.template?.display_name ||
        component.template?.name ||
        'Template Document'
      );
    case 'static_document':
      if (component.static_document_tag) {
        return StaticDocumentLabels[component.static_document_tag];
      }
      return 'Static Document';
    case 'matter_blob':
      return (
        component.blob?.original_filename ||
        component.blob?.name ||
        'Matter Document'
      );
    case 'signed_authorization_merge':
      return 'Signed Authorization';
    default:
      return 'Document';
  }
};

export const getEditablePackageComponents = (
  components: PackageComponentSchema[] | undefined,
): PackageComponentSchema[] =>
  (components ?? []).filter(isOnlyOfficeEditablePackageComponent);

export const getVisiblePackageComponents = (
  components: PackageComponentSchema[] | undefined,
): PackageComponentSchema[] =>
  (components ?? []).filter(isVisiblePackageComponent);

const MISSING_FIELD_LABELS: Partial<Record<string, string>> = {
  clientSsn: 'patient ssn',
  medicalAddress: 'provider address',
  medicalPhone: 'provider phone',
  treatmentChannelType: 'request method',
  treatmentChannelValue: 'request method',
  clientHipaaSignedDate: 'HIPAA signature',
};

const humanizeFallbackLabel = (rawLabel: string): string =>
  rawLabel
    .replace(/([a-z0-9])([A-Z])/g, '$1 $2')
    .replace(/[_-]+/g, ' ')
    .trim()
    .toLowerCase();

export const humanizeMissingFieldId = (fieldId: string): string => {
  const mappedLabel = MISSING_FIELD_LABELS[fieldId];
  if (mappedLabel) return mappedLabel;

  return humanizeFallbackLabel(fieldId || 'required field');
};

export const humanizeMissingField = (field: MissingFieldInfo): string => {
  const key = field.context_key || field.field_name || field.field_id;
  if (key) {
    const mappedLabel = MISSING_FIELD_LABELS[key];
    if (mappedLabel) return mappedLabel;
  }

  const rawLabel = field.field_name || field.context_key || field.field_id;
  return humanizeFallbackLabel(rawLabel || 'required field');
};

export interface ComponentMissingInfoItem {
  key: string;
  fieldId: string;
  label: string;
  baseLabel: string;
  componentId: string;
  componentTitle: string;
  canDismiss: boolean;
  resolvedMissingFields: string[];
}

export interface ComponentResolvedInfoItem {
  key: string;
  fieldId: string;
  label: string;
  baseLabel: string;
  componentId: string;
  componentTitle: string;
  resolvedMissingFields: string[];
}

export const hasManualComponentContent = (
  component: PackageComponentSchema,
): boolean =>
  Boolean(
    component.has_manual_edits ||
      (component.resolved_missing_fields?.length ?? 0) > 0,
  );

export const hasManualPackageContent = (
  components: PackageComponentSchema[] | undefined,
): boolean => (components ?? []).some(hasManualComponentContent);

export const getComponentMissingInfoItems = (
  components: PackageComponentSchema[] | undefined,
): ComponentMissingInfoItem[] => {
  const baseItems = (components ?? []).flatMap((component) => {
    if (component.component_type === 'signed_authorization_merge') {
      return [];
    }

    const entries = Object.entries(component.missing_fields ?? {});
    if (entries.length === 0) {
      return [];
    }

    const componentTitle = getPackageComponentTitle(component);
    const resolvedMissingFields = component.resolved_missing_fields ?? [];

    return entries.map(([fieldId, field]) => ({
      key: `${component.id}:${fieldId}`,
      fieldId,
      baseLabel: humanizeMissingField(field),
      componentId: String(component.id),
      componentTitle,
      canDismiss: true,
      resolvedMissingFields,
    }));
  });

  const labelCounts = baseItems.reduce<Map<string, number>>((counts, item) => {
    counts.set(item.baseLabel, (counts.get(item.baseLabel) ?? 0) + 1);
    return counts;
  }, new Map());

  return baseItems.map((item) => ({
    ...item,
    label:
      (labelCounts.get(item.baseLabel) ?? 0) > 1
        ? `${item.baseLabel} • ${item.componentTitle}`
        : item.baseLabel,
  }));
};

export const getComponentResolvedInfoItems = (
  components: PackageComponentSchema[] | undefined,
): ComponentResolvedInfoItem[] => {
  const baseItems = (components ?? []).flatMap((component) => {
    if (component.component_type === 'signed_authorization_merge') {
      return [];
    }

    const resolvedMissingFields = component.resolved_missing_fields ?? [];
    const resolvedMissingFieldLabels =
      component.resolved_missing_field_labels ?? {};
    if (resolvedMissingFields.length === 0) {
      return [];
    }

    const componentTitle = getPackageComponentTitle(component);

    return resolvedMissingFields.map((fieldId) => ({
      key: `${component.id}:${fieldId}`,
      fieldId,
      baseLabel: humanizeMissingFieldId(
        resolvedMissingFieldLabels[fieldId] || fieldId,
      ),
      componentId: String(component.id),
      componentTitle,
      resolvedMissingFields,
    }));
  });

  const labelCounts = baseItems.reduce<Map<string, number>>((counts, item) => {
    counts.set(item.baseLabel, (counts.get(item.baseLabel) ?? 0) + 1);
    return counts;
  }, new Map());

  return baseItems.map((item) => ({
    ...item,
    label:
      (labelCounts.get(item.baseLabel) ?? 0) > 1
        ? `${item.baseLabel} • ${item.componentTitle}`
        : item.baseLabel,
  }));
};

const isBlockingComponent = (
  componentType: string | null | undefined,
): boolean => componentType !== 'signed_authorization_merge';

export const hasBlockingMissingFields = (
  components: PackageComponentSchema[] | undefined,
): boolean =>
  (components ?? []).some(
    (component) =>
      isBlockingComponent(component.component_type) &&
      component.missing_fields &&
      Object.keys(component.missing_fields).length > 0,
  );

export const hasBlockingValidationMissingFields = (
  components: ComponentValidationResult[] | undefined,
): boolean =>
  (components ?? []).some(
    (component) =>
      isBlockingComponent(component.component_type) &&
      component.missing_fields &&
      Object.keys(component.missing_fields).length > 0,
  );

export const isPackageRendering = (
  packageData:
    | Pick<HipaaPackageSchema, 'status' | 'components'>
    | null
    | undefined,
): boolean => {
  if (!packageData) return false;

  return (
    packageData.status === 'generating' ||
    (packageData.components ?? []).some(
      (component) => component.status === 'generating',
    )
  );
};
